import React, { useLayoutEffect, useRef, useState } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

import { Button } from "@/components/ui";

import { cx } from "@/lib/tailwind-utils";

export const DEFAULT_PER_PAGE = 25;

interface Props {
  displayText: string;
  total: number;
  currentPage: number;
  perPage: number;
  handlePageChange: (direction: "prev" | "next") => void;
  className?: string;
}

const Pagination: React.FC<Props> = ({
  displayText,
  total,
  currentPage,
  perPage = DEFAULT_PER_PAGE,
  handlePageChange,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  useLayoutEffect(() => {
    const updateLayout = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        // Switch to compact mode if container is less than 600px wide
        setIsCompact(width < 600);
      }
    };

    updateLayout();

    const resizeObserver = new ResizeObserver(updateLayout);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === Math.ceil(total / perPage);

  return (
    <div
      ref={containerRef}
      className={cx(
        "flex w-full flex-row items-center justify-between gap-4 px-6 py-4 md:justify-center",
        className,
      )}
    >
      <Button
        variant="secondary"
        onClick={() => handlePageChange("prev")}
        disabled={isFirstPage || total === 0}
        // Compact mode drops the word for a chevron, which leaves the button
        // with no accessible name unless one is stated.
        aria-label={isCompact ? "Previous" : undefined}
        className={cx(
          "rounded-lg border  text-body text-foreground-secondary shadow-sm transition-colors duration-200",
          isCompact ? "h-8 w-8 p-0" : "px-4 py-2",
          isFirstPage || total === 0
            ? "cursor-not-allowed opacity-50"
            : "hover:border-border hover:bg-row-hover",
        )}
      >
        {isCompact ? <HiChevronLeft className="shrink-0" /> : "Previous"}
      </Button>

      <span
        className={cx("text-body", isCompact ? "text-center" : "text-center")}
      >
        {total === 0 ? (
          <>
            Showing <span className="font-semibold">0</span> of{" "}
            <span className="font-semibold">0</span> {displayText}
          </>
        ) : (
          <>
            {isCompact ? "Showing " : "Viewing "}
            <span className="font-semibold">
              {((currentPage - 1) * perPage + 1).toLocaleString()}
            </span>
            <span>-</span>
            <span className="font-semibold">
              {Math.min(currentPage * perPage, total).toLocaleString()}
            </span>
            <span> </span>
            <span>of </span>
            <span className="font-semibold">{total.toLocaleString()}</span>{" "}
            {isCompact ? "records" : displayText}
          </>
        )}
      </span>

      <Button
        variant="secondary"
        onClick={() => handlePageChange("next")}
        disabled={isLastPage || total === 0}
        aria-label={isCompact ? "Next" : undefined}
        className={cx(
          "rounded-lg border  text-body text-foreground-secondary shadow-sm transition-colors duration-200",
          isCompact ? "h-8 w-8 p-0" : "px-4 py-2",
          isLastPage || total === 0
            ? "cursor-not-allowed opacity-50"
            : "hover:border-border hover:bg-row-hover",
        )}
      >
        {isCompact ? <HiChevronRight className="shrink-0" /> : "Next"}
      </Button>
    </div>
  );
};

export default Pagination;
